/**
 * Initialises i18next. Imported once, for its side effect, from `main.tsx`.
 *
 * There is no React context here and none is needed: `react-i18next` subscribes
 * components through `useTranslation`, so a language change re-renders exactly
 * the components that read a string. The only state this module owns is the
 * remembered choice, which follows `ColorModeProvider`'s pattern — localStorage,
 * failing softly, because not being able to *remember* the choice must never
 * stop it taking effect.
 *
 * **A component that formats text but calls no `t()` will not re-render on a
 * language change.** `Timestamp` is the one that bites: it renders through
 * `utils/format`, so it calls `useTranslation()` purely to subscribe.
 */
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import {
  FALLBACK_LANGUAGE,
  LANGUAGE_STORAGE_KEY,
  NAMESPACES,
  isLanguageCode,
  type LanguageCode,
} from './config';
import { resources } from './resources';
import { MODULE_NAMESPACES } from '../modules/locales';

/**
 * The remembered choice, then the browser's, then English.
 *
 * Browser tags are matched on their base — `cs-CZ` is Czech — because this app
 * ships languages, not regional variants.
 */
function detectLanguage(): LanguageCode {
  try {
    const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (isLanguageCode(stored)) return stored;
  } catch {
    // Private browsing, or storage disabled. Fall through to the browser's.
  }

  const tags = navigator.languages?.length ? navigator.languages : [navigator.language];
  for (const tag of tags) {
    const base = tag.split('-')[0]?.toLowerCase();
    if (isLanguageCode(base)) return base;
  }
  return FALLBACK_LANGUAGE;
}

const initial = detectLanguage();

void i18n.use(initReactI18next).init({
  resources,
  lng: initial,
  fallbackLng: FALLBACK_LANGUAGE,
  ns: [...NAMESPACES, ...MODULE_NAMESPACES],
  defaultNS: 'common',
  // React escapes everything it renders. Escaping again turns an apostrophe
  // into `&#39;` on screen.
  interpolation: { escapeValue: false },
  returnNull: false,
});

applyDocumentLanguage(initial);

/**
 * Tells the browser what language the page is in.
 *
 * Not cosmetic: it is what a screen reader picks a voice from, and what
 * hyphenation and spellcheck read.
 */
function applyDocumentLanguage(code: LanguageCode): void {
  document.documentElement.lang = code;
}

/** Switches language, remembers it, and updates `<html lang>`. */
export async function changeLanguage(code: LanguageCode): Promise<void> {
  await i18n.changeLanguage(code);
  applyDocumentLanguage(code);
  try {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, code);
  } catch {
    // Not being able to remember the choice must not stop it taking effect.
  }
}

/** The active language, narrowed to one this build actually ships. */
export function currentLanguage(): LanguageCode {
  return isLanguageCode(i18n.language) ? i18n.language : FALLBACK_LANGUAGE;
}

export default i18n;
